import { inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable, of } from 'rxjs';
import { SupabaseService } from '../supabase/supabase';
import { AuthService } from '../auth/auth.service';
import { ApiService } from '../api.service';
import { PERCORSI_INTERVISTA } from '../../shared/helpers/question-set.helper';
import { doSupabaseCall } from '../../shared/helpers/supabase.helper';
import type {
  IAnswerRow,
  IInterviewStart,
  ILookupAmbiente,
  ILookupImpianto,
  ILookupImpiantoOpzione,
  ILookupPercorso,
  ILookups,
  IQuestionSet,
  IQuestionSetVersion,
  PercorsoIntervista,
} from '../../shared/models';

// Righe di interview_question_set_app con le due FK verso select_percorso risolte a code.
interface IStartRow {
  fk_question_set_app_id: string;
  override_reason: string | null;
  proposto: { code: PercorsoIntervista } | null;
  scelto: { code: PercorsoIntervista } | null;
}
interface ICodeJoin {
  lookup: { code: string } | null;
}

// Chiave dell'upsert su interview_answer_app (UQ_interview_answer_app_key, 0031). Madre o
// sottodomanda: fk_question_app_id punta direttamente alla voce risposta (tabella unica).
// Riga di select_impianto_opzione con l'impianto risolto a code (embed PostgREST).
interface IOpzioneRow extends ILookupImpiantoOpzione {
  impianto: { code: string } | null;
}

const ANSWER_KEY = 'fk_interview_app_id,fk_participant_app_id,fk_question_app_id';

// Questionario relazionale (migrazione 0031): il set si legge dal BE (ApiService, decisione
// 11/09: niente funzioni Postgres); lookup, avvio fissato e risposte riga-per-riga via Supabase,
// solo ciò che la RLS lascia fare al FE. Le SCRITTURE di set e avvio intervista passano dal BE.
@Injectable({ providedIn: 'root' })
export class QuestionSetService {
  // --- private ---
  private readonly _supabase: SupabaseService = inject(SupabaseService);
  private readonly _auth: AuthService = inject(AuthService);
  private readonly _api: ApiService = inject(ApiService);

  // --- public ---
  // Set annidato (domande → sottodomande, percorsi/ambienti/impianti come codici).
  // setId null = set corrente; un id = la versione fissata a un'intervista da riprendere.
  // null anche se nessuna versione è stata ancora pubblicata (404 del BE).
  public getQuestionSet(setId: string | null = null): Observable<IQuestionSet | null> {
    return this._api.getQuestionSet(setId);
  }

  // Storico versioni (più recente prima): per il dialog "Storico versioni" dell'admin.
  public listVersions(limit = 20): Observable<IQuestionSetVersion[]> {
    const client = this._supabase.client;
    if (null === client) {
      return of([]);
    }
    return doSupabaseCall<IQuestionSetVersion[]>(
      client
        .from('question_set_app')
        .select('id, version, note, date_cre')
        .order('version', { ascending: false })
        .limit(limit)
        .returns<IQuestionSetVersion[]>(),
    );
  }

  // Le tre lookup attive, in ordine di sort_order. I percorsi sono filtrati anche su
  // PERCORSI_INTERVISTA: "media" resta a DB ma nascosto solo lato FE (scelta 22/09/2026, così
  // si riattiva rimettendolo nella costante, senza migrazione).
  public getLookups(): Observable<ILookups> {
    const client = this._supabase.client;
    if (null === client) {
      return of({ percorsi: [], ambienti: [], impianti: [] });
    }
    return forkJoin({
      percorsi: doSupabaseCall<ILookupPercorso[]>(
        client
          .from('select_percorso')
          .select('code, name, description, sort_order')
          .eq('is_active', true)
          .in('code', [...PERCORSI_INTERVISTA])
          .order('sort_order')
          .returns<ILookupPercorso[]>(),
      ),
      ambienti: doSupabaseCall<ILookupAmbiente[]>(
        client
          .from('select_ambiente')
          .select('code, name, sort_order')
          .eq('is_active', true)
          .order('sort_order')
          .returns<ILookupAmbiente[]>(),
      ),
      impianti: forkJoin({
        rows: doSupabaseCall<Omit<ILookupImpianto, 'opzioni'>[]>(
          client
            .from('select_impianto')
            .select('code, name, sort_order')
            .eq('is_active', true)
            .order('sort_order')
            .returns<Omit<ILookupImpianto, 'opzioni'>[]>(),
        ),
        // Opzioni "tipo di intervento" (0034), agganciate all'impianto per code.
        opzioni: doSupabaseCall<IOpzioneRow[]>(
          client
            .from('select_impianto_opzione')
            .select('code, name, sort_order, impianto:select_impianto!inner(code)')
            .eq('is_active', true)
            .order('sort_order')
            .returns<IOpzioneRow[]>(),
        ),
      }).pipe(
        map(({ rows, opzioni }) =>
          rows.map((r) => ({
            ...r,
            opzioni: opzioni
              .filter((o) => o.impianto?.code === r.code)
              .map(({ code, name, sort_order }) => ({ code, name, sort_order })),
          })),
        ),
      ),
    });
  }

  // Cosa è stato fissato all'avvio (set, percorsi, perimetro); null se non ancora avviata.
  public getInterviewStart(interviewId: string): Observable<IInterviewStart | null> {
    const client = this._supabase.client;
    if (null === client) {
      return of(null);
    }
    const toCodes = (rows: ICodeJoin[]): string[] =>
      rows.map((r) => r.lookup?.code).filter((c): c is string => undefined !== c);
    return forkJoin({
      start: doSupabaseCall<IStartRow | null>(
        client
          .from('interview_question_set_app')
          .select(
            'fk_question_set_app_id, override_reason, proposto:select_percorso!fk_select_percorso_id_proposto(code), scelto:select_percorso!fk_select_percorso_id_scelto(code)',
          )
          .eq('fk_interview_app_id', interviewId)
          .maybeSingle(),
      ),
      ambienti: doSupabaseCall<ICodeJoin[]>(
        client
          .from('interview_app_select_ambiente')
          .select('lookup:select_ambiente(code)')
          .eq('fk_interview_app_id', interviewId)
          .returns<ICodeJoin[]>(),
      ).pipe(map(toCodes)),
      impianti: doSupabaseCall<ICodeJoin[]>(
        client
          .from('interview_app_select_impianto')
          .select('lookup:select_impianto(code)')
          .eq('fk_interview_app_id', interviewId)
          .returns<ICodeJoin[]>(),
      ).pipe(map(toCodes)),
    }).pipe(
      map(({ start, ambienti, impianti }) => {
        const proposto = start?.proposto?.code;
        const scelto = start?.scelto?.code;
        if (null === start || undefined === proposto || undefined === scelto) {
          return null;
        }
        return {
          question_set_id: start.fk_question_set_app_id,
          percorso_proposto: proposto,
          percorso_scelto: scelto,
          motivazione: start.override_reason,
          ambienti,
          impianti,
        };
      }),
    );
  }

  // Una risposta = una riga; upsert sulla chiave (intervista, partecipante, voce).
  public upsertAnswers(rows: IAnswerRow[]): Observable<void> {
    const client = this._supabase.client;
    const session = this._auth.sessionSignal();
    if (null === client || null === session || 0 === rows.length) {
      return of(undefined);
    }
    const userId = session.user.id;
    return doSupabaseCall(
      client.from('interview_answer_app').upsert(
        rows.map((r) => ({ ...r, fk_auth_users_id_cre: userId })),
        { onConflict: ANSWER_KEY },
      ),
    ).pipe(map(() => undefined));
  }

  public getAnswers(interviewId: string): Observable<IAnswerRow[]> {
    const client = this._supabase.client;
    if (null === client) {
      return of([]);
    }
    return doSupabaseCall<IAnswerRow[]>(
      client
        .from('interview_answer_app')
        .select('fk_interview_app_id, fk_participant_app_id, fk_question_app_id, answer, extra')
        .eq('fk_interview_app_id', interviewId),
    );
  }
}
